import React, { useMemo } from 'react';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import styled, { css } from 'styled-components';
import { Colors, Typography, Button, Loader, Checkbox, Option } from '@inplayer-org/inplayer-ui';

// types
import { PackageAssetDetails } from 'ducks/merchantDucks/packages/types';

// components
import Filter from 'components/Filter';
import { Optional } from 'utils/types';
import { BlueTypography } from 'components/SharedStyledElements';
import SearchTooltip from 'components/SearchTooltip';
import { snakeCase } from 'lodash';
import AssetItem from './AssetItem';
import { DroppableNames } from './ManageAssets';

const Container = styled.div`
  width: 48%;
  height: 100%;
`;

const DroppableContainer = styled.div`
  padding: 1rem;
  border: 1px solid ${Colors.fontLightGray};
  border-radius: 0.3rem;
  width: 100%;
  height: 75%;
  overflow: scroll;
  margin-top: 1rem;
`;

export const LoaderContainer = styled.div`
  margin: auto;
  width: 100%;
  display: flex;
  justify-content: center;
  height: 100%;
  align-items: center;
`;

const sharedMargin = () => css`
  margin: 1rem 0;
`;

const StyledButton = styled(Button)`
  margin-top: 1rem;
`;

const Title = styled(BlueTypography)`
  ${sharedMargin}
`;

const CheckboxStyled = styled(Checkbox)`
  ${sharedMargin}
`;

const Description = styled(Typography)`
  font-style: italic;
  color: ${Colors.asbestos};
  font-size: ${({ theme }) => theme.font.sizes.small};
`;

const MarginWrapper = styled.div`
  > * {
    margin-top: 1rem;
  }
`;

const DraggableWrapper = styled.div<{ isDragged: boolean }>`
  ${({ isDragged }) => isDragged && 'visibility: hidden'};
`;

enum BulkAssetsActionType {
  Removed = 'removed',
  Added = 'added',
}

const suggestions: Array<Option> = [
  { value: 'id', displayName: 'ID' },
  { value: 'title', displayName: 'Name' },
];

interface SearchParams {
  id: Optional<number>;
  title: string;
}

interface OwnProps {
  droppableId: string;
  items: Array<PackageAssetDetails>;
  isDragDisabled?: boolean;
  title: string;
  buttonText: string;
  buttonAction: (droppableId: string) => void;
  isLoaderVisible: boolean;
  totalPackageAssets: number;
  isReadOnly?: boolean;
  handleAddition: (tag: any) => void;
  isDroppableDisabled?: boolean;
  filter: SearchParams;
  clearFilter: (filter: any) => void;
  showCustomButton?: boolean;
  customButtonText?: string;
  customButtonAction?: () => void;
  isCheckedAll: boolean;
  toggleCheckbox: (id: number) => any;
  checkedAssets: any;
  onCheckAllAssets: (i: boolean) => any;
  isCheckboxDisabled: boolean;
  assetsAmount: number;
  draggedItemId?: string;
}

type Props = OwnProps;

const DroppableColumn = ({
  droppableId,
  items,
  isDragDisabled = false,
  title,
  handleAddition,
  buttonText,
  buttonAction,
  isLoaderVisible,
  totalPackageAssets,
  isReadOnly = false,
  isDroppableDisabled = false,
  showCustomButton = false,
  customButtonText,
  customButtonAction,
  isCheckedAll,
  toggleCheckbox,
  checkedAssets,
  onCheckAllAssets,
  isCheckboxDisabled,
  assetsAmount,
  filter,
  clearFilter,
  draggedItemId,
}: Props) => {
  const isDragged = (id: number) => {
    if (checkedAssets[id] && draggedItemId && checkedAssets[draggedItemId]) {
      return Number(draggedItemId) !== id;
    }
    return false;
  };

  const totalCheckedAssets = Object.keys(checkedAssets).filter((key) => checkedAssets[key]).length;
  const lowercasedDroppableId = droppableId.toLowerCase();

  // Rerender these items only if the list of items changes
  const itemsForRender = useMemo(() => {
    return items.map((item: PackageAssetDetails, index: number) => {
      return (
        <DraggableWrapper key={item.id} isDragged={isDragged(item.id)}>
          <Draggable draggableId={String(item.id)} index={index} isDragDisabled={isDragDisabled}>
            {(providedDraggable, snapshot) => (
              <AssetItem
                assetStatus={lowercasedDroppableId}
                ref={providedDraggable.innerRef}
                {...providedDraggable.draggableProps}
                {...providedDraggable.dragHandleProps}
                title={item.title}
                image={item.metahash ? item.metahash.paywall_cover_photo : ''}
                id={item.id}
                checkedAssets={checkedAssets}
                toggleCheckbox={toggleCheckbox}
                isDraggingMultiple={snapshot.isDragging && checkedAssets[item.id] && totalCheckedAssets > 1}
                totalItems={totalCheckedAssets}
                isDragged={isDragged(item.id)}
                isFirstItem={index === 0}
              />
            )}
          </Draggable>
        </DraggableWrapper>
      );
    });
  }, [items, checkedAssets, draggedItemId]);

  const handleButtonAction = () => {
    buttonAction(droppableId);
  };

  const bulkAssetsActionType =
    droppableId === DroppableNames.added ? BulkAssetsActionType.Removed : BulkAssetsActionType.Added;

  return (
    <Container>
      <Title variant="h4">{title}</Title>
      <SearchTooltip
        tag={`tooltip_search_manage_assets_${lowercasedDroppableId}`}
        placement={droppableId === DroppableNames.added ? 'bottom' : 'right'}
      />
      <MarginWrapper>
        <Filter
          options={suggestions}
          onSearch={handleAddition}
          externalFilters={filter}
          clearExternalFilter={clearFilter}
          dropdownTag={`dropdown_search_${lowercasedDroppableId}`}
          inputTag={`text_search_${lowercasedDroppableId}`}
        />
        <CheckboxStyled
          label="Select all loaded assets"
          name="all"
          id={droppableId}
          checked={isCheckedAll}
          onChange={onCheckAllAssets}
          disabled={isCheckboxDisabled}
          tag={`checkbox_select_all_${lowercasedDroppableId}`}
        />
        {showCustomButton && (
          <StyledButton
            tag={`button_${snakeCase(customButtonText)}`}
            onClick={customButtonAction}
            type="button"
            disabled={!items.length}
          >
            {customButtonText}
          </StyledButton>
        )}
        <Description variant="p">
          {assetsAmount} assets will be {bulkAssetsActionType}
        </Description>
      </MarginWrapper>
      <Droppable droppableId={droppableId} isDropDisabled={isDroppableDisabled}>
        {(provided) => (
          <DroppableContainer ref={provided.innerRef} {...provided.droppableProps}>
            {isLoaderVisible ? (
              <LoaderContainer>
                <Loader />
              </LoaderContainer>
            ) : (
              (itemsForRender.length > 0 && itemsForRender) || 'No assets added yet.'
            )}
            {provided.placeholder}
          </DroppableContainer>
        )}
      </Droppable>
      <StyledButton
        tag={`button_load_${lowercasedDroppableId}`}
        onClick={handleButtonAction}
        fullWidth
        type="button"
        buttonModifiers={['buttonLink']}
        disabled={isReadOnly || totalPackageAssets <= items.length || totalPackageAssets === 0}
      >
        {buttonText}
      </StyledButton>
    </Container>
  );
};

export default DroppableColumn;
